import type { StaffRole } from '@planuze/admin-contracts';
import {
  BadgeCheck,
  BarChart3,
  Bell,
  BookOpen,
  Building2,
  CreditCard,
  Download,
  FileText,
  HelpCircle,
  LayoutDashboard,
  type LucideIcon,
  MessageSquare,
  Package,
  PackageCheck,
  RefreshCw,
  Scale,
  ShieldCheck,
  SlidersHorizontal,
  UserCog,
  Users,
} from 'lucide-react';

/* Caminhos registrados na árvore de rotas (router.tsx). Tipar `to` com este union
   (em vez de `string`) mantém o `<Link>` do TanStack type-safe. */
export type AdminRoutePath =
  | '/dashboard'
  | '/companies'
  | '/users'
  | '/plans'
  | '/plans/new'
  | '/plans/new-targeted'
  | '/support'
  | '/metrics'
  | '/downloads'
  | '/pack-submissions'
  | '/packs'
  | '/publishers'
  | '/moderation-config'
  | '/publisher-guide'
  | '/faq'
  | '/notifications'
  | '/legal'
  | '/releases'
  | '/releases/register'
  | '/staff'
  | '/settings'
  | '/audit';

/* Navegação declarativa do backoffice (§4.18): cada item carrega os roles que o
   enxergam; o shell filtra por `session.role` em vez de ramificar em JSX. `'all'`
   = visível a todos os tiers. Páginas reais entram em 28-11..15 (rotas placeholder
   até lá não quebram o filtro). */
/* Seções da sidebar (ADR-0700) — agrupam os itens por domínio em vez de uma lista plana de 14
   entradas. A ordem aqui é a ordem de exibição. */
/* Identificadores, não texto de tela. Eram palavras em português — o que os fazia parecer
   copy e reprovava a regra de naming ao virarem chave de catálogo. Quem os EXIBE é
   `content/sections/nav.ts`. */
export type NavGroup = 'general' | 'customers' | 'marketplace' | 'support' | 'operations';
const NAV_GROUP_ORDER: readonly NavGroup[] = [
  'general',
  'customers',
  'marketplace',
  'support',
  'operations',
];

export interface NavItem {
  readonly to: AdminRoutePath;
  readonly icon: LucideIcon;
  readonly roles: 'all' | readonly StaffRole[];
  readonly group: NavGroup;
}

export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/dashboard', icon: LayoutDashboard, roles: 'all', group: 'general' },
  { to: '/metrics', icon: BarChart3, roles: 'all', group: 'general' },
  { to: '/downloads', icon: Download, roles: 'all', group: 'general' },
  { to: '/companies', icon: Building2, roles: 'all', group: 'customers' },
  { to: '/users', icon: Users, roles: 'all', group: 'customers' },
  {
    to: '/plans',
    icon: CreditCard,
    roles: ['super_admin', 'billing_ops', 'readonly'],
    group: 'customers',
  },
  { to: '/packs', icon: Package, roles: 'all', group: 'marketplace' },
  {
    to: '/publishers',
    icon: BadgeCheck,
    roles: ['super_admin', 'support'],
    group: 'marketplace',
  },
  {
    to: '/pack-submissions',
    icon: PackageCheck,
    roles: ['super_admin', 'support'],
    group: 'marketplace',
  },
  {
    to: '/moderation-config',
    icon: ShieldCheck,
    roles: ['super_admin', 'support'],
    group: 'marketplace',
  },
  {
    to: '/publisher-guide',
    icon: BookOpen,
    roles: ['super_admin', 'support'],
    group: 'marketplace',
  },
  {
    to: '/support',
    icon: MessageSquare,
    roles: ['super_admin', 'support'],
    group: 'support',
  },
  {
    to: '/faq',
    icon: HelpCircle,
    roles: ['super_admin', 'support'],
    group: 'support',
  },
  {
    to: '/notifications',
    icon: Bell,
    roles: ['super_admin', 'support'],
    group: 'support',
  },
  {
    to: '/releases',
    icon: RefreshCw,
    roles: ['super_admin'],
    group: 'operations',
  },
  /* READ é dos 4 tiers (quem atende usuário precisa ler o texto vigente); a restrição a
     super_admin vale para PUBLICAR/remover e é aplicada dentro da página, não na navegação. */
  { to: '/legal', icon: Scale, roles: 'all', group: 'operations' },
  {
    to: '/staff',
    icon: UserCog,
    roles: ['super_admin'],
    group: 'operations',
  },
  {
    to: '/settings',
    icon: SlidersHorizontal,
    roles: ['super_admin'],
    group: 'operations',
  },
  { to: '/audit', icon: FileText, roles: 'all', group: 'operations' },
];

export function filterNavByRole(items: readonly NavItem[], role: StaffRole): readonly NavItem[] {
  return items.filter((item) => item.roles === 'all' || item.roles.includes(role));
}

export interface NavSection {
  readonly group: NavGroup;
  readonly items: readonly NavItem[];
}

/** Filtra por role e agrupa por seção (na ordem de `NAV_GROUP_ORDER`); pula seções vazias. */
export function sectionsForRole(role: StaffRole): readonly NavSection[] {
  const visible = filterNavByRole(NAV_ITEMS, role);
  return NAV_GROUP_ORDER.map((group) => ({
    group,
    items: visible.filter((item) => item.group === group),
  })).filter((section) => section.items.length > 0);
}
